import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Layout from '../../components/Layout';
import ShareButtons from '../../components/ShareButtons';

// Simple frontmatter parser to avoid buffer dependency
function parseFrontmatter(text) {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = text.match(frontmatterRegex);

  if (!match) {
    return { data: {}, content: text };
  }

  const frontmatterText = match[1];
  const content = match[2];

  // Parse YAML-like frontmatter
  const data = {};
  frontmatterText.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim();
      let value = line.substring(colonIndex + 1).trim();

      // Remove quotes if present
      if ((value.startsWith('"') && value.endsWith('"')) ||
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }

      // Parse arrays
      if (value.startsWith('[') && value.endsWith(']')) {
        value = value.slice(1, -1).split(',').map(v => v.trim().replace(/['"]/g, ''));
      }

      data[key] = value;
    }
  });

  return { data, content };
}

function BlogPost() {
  const { slug } = useParams();
  const [content, setContent] = useState('');
  const [frontmatter, setFrontmatter] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current URL for sharing
  const currentUrl = window.location.href;

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/blog/${slug}.md`);

        if (!response.ok) {
          throw new Error('Post not found');
        }

        const text = await response.text();
        const { data, content: markdownContent } = parseFrontmatter(text);

        setFrontmatter(data);
        setContent(markdownContent);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <Typography sx={{ color: 'text.secondary' }}>Loading...</Typography>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Typography sx={{ color: 'text.secondary' }}>Error: {error}</Typography>
        <Button component={Link} to="/" sx={{ marginTop: 2 }}>
          Back to Blog
        </Button>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{frontmatter.title ? `${frontmatter.title} — Rojan Thomas` : 'Blog — Rojan Thomas'}</title>
        {frontmatter.description && <meta name="description" content={frontmatter.description} />}
      </Helmet>
      {/* Back button */}
      <Button
        component={Link}
        to="/"
        sx={{
          marginBottom: 3,
          color: 'text.secondary',
          fontSize: '0.9rem',
          padding: 0,
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'text.primary',
          },
        }}
      >
        ← Back to Blog
      </Button>

      {/* Post header */}
      <Box
        sx={{
          marginBottom: 4,
          paddingBottom: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '1.75rem', sm: '2rem' },
            fontWeight: 600,
            marginBottom: 2,
          }}
        >
          {frontmatter.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.9rem',
            marginBottom: 2,
          }}
        >
          {frontmatter.date && new Date(frontmatter.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </Typography>

        {/* Share buttons */}
        <ShareButtons title={frontmatter.title} url={currentUrl} />
      </Box>

      {/* Hero image */}
      {frontmatter.heroImage && (
        <Box
          sx={{
            marginBottom: 4,
            width: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <img
            src={frontmatter.heroImage}
            alt={frontmatter.title}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
          />
        </Box>
      )}

      {/* Markdown content */}
      <Box
        sx={{
          '& h1': {
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            fontWeight: 600,
            marginTop: 4,
            marginBottom: 2,
            color: 'text.primary',
          },
          '& h2': {
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 600,
            marginTop: 3,
            marginBottom: 1.5,
            color: 'text.primary',
          },
          '& h3': {
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: 600,
            marginTop: 2.5,
            marginBottom: 1,
            color: 'text.primary',
          },
          '& p': {
            fontSize: '0.95rem',
            lineHeight: 1.7,
            marginBottom: 2,
            color: 'text.primary',
          },
          '& ul, & ol': {
            marginBottom: 2,
            paddingLeft: 3,
            '& li': {
              fontSize: '0.95rem',
              lineHeight: 1.7,
              marginBottom: 0.5,
              color: 'text.primary',
            },
          },
          '& code': {
            fontFamily: '"JetBrains Mono", "IBM Plex Mono", monospace',
            fontSize: '0.9rem',
            backgroundColor: 'action.hover',
            padding: '2px 6px',
            borderRadius: '4px',
          },
          '& pre': {
            backgroundColor: 'action.hover',
            padding: 2,
            borderRadius: '4px',
            overflow: 'auto',
            marginBottom: 2,
            '& code': {
              backgroundColor: 'transparent',
              padding: 0,
            },
          },
          '& a': {
            color: 'text.primary',
            textDecoration: 'underline',
            '&:hover': {
              opacity: 0.7,
            },
          },
          '& blockquote': {
            borderLeft: '3px solid',
            borderColor: 'divider',
            paddingLeft: 2,
            marginLeft: 0,
            marginBottom: 2,
            color: 'text.secondary',
            fontStyle: 'italic',
          },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {content}
        </ReactMarkdown>
      </Box>

    </Layout>
  );
}

export default BlogPost;
